import { getCompanyNetwork, getBusinessByIdentifier } from 'lib/api';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';
import { RootState } from 'services/redux/store';

const headers = [
  { key: 'name', label: 'Empresa' },
  { key: 'identifier', label: 'Rut' },
];

const NetworkBusinesses: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const refreshTrigger = useSelector((state: RootState) => state.businessNetwork.refreshTrigger);
  const lastRefresh = useSelector((state: RootState) => state.businessNetwork.lastRefresh);
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');

  // Check if user has permission to view network
  const canEditBusinessNamePermission = validatePermissions(user, 'can_edit_business_name');

  const setUp = async () => {
    try {
      const businessesResponse = await getCompanyNetwork(dispatch, '');
      setBusinesses(businessesResponse.data);
      setPagination(businessesResponse.pagination);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  useEffect(() => {
    if (lastRefresh > 0) {
      console.log('Refreshing business list...');
      void setUp();
    }
  }, [lastRefresh]);

  useEffect(() => {
    const handleBusinessNameUpdated = () => {
      void setUp();
    };

    window.addEventListener('businessNameUpdated', handleBusinessNameUpdated);
    
    return () => {
      window.removeEventListener('businessNameUpdated', handleBusinessNameUpdated);
    };
  }, []);

  const handleDetails = async (id: number) => {
    const business = businesses.find((x: any) => x.id === id);
    if (!business?.identifier || !business?.name) return;
    
    try {
      // Get the full business data including the real database ID
      const businessData = await getBusinessByIdentifier(dispatch, {
        identifier: business.identifier,
      });
      
      if (businessData) {
        dispatch(
          showBusinessNetwork({
            id: businessData.identifier, // Use the real database ID
            name: business.name,
            canEditBusinessName: canEditBusinessNamePermission,
          }),
        );
      }
    } catch (error) {
      console.error('Error fetching business data:', error);
      // Fallback to using the identifier if the API call fails
      dispatch(
        showBusinessNetwork({
          id: business.identifier,
          name: business.name,
          canEditBusinessName: canEditBusinessNamePermission,
        }),
      );
    }
  };

  const actions = [
    {
      action: handleDetails,
      actionLabel: 'Ver',
    },
  ];

  const generateQuery = ({
    page,
    pageSize,
    businessIdentifier,
    businessName,
  }: {
    page: number;
    pageSize: number;
    businessIdentifier: string;
    businessName: string;
  }) => {
    let query = '';
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName) query += `businessName=${businessName.replace('&', '%26')}&`;
    if (page && pageSize) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const searchNetwork = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({
        page,
        pageSize,
        businessIdentifier,
        businessName,
      });
      const response = await getCompanyNetwork(dispatch, query);
      setBusinesses(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const triggerSearch = async () => {
    setLoading(true);
    await searchNetwork({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const filters: Filter[] = [
    {
      label: 'Nombre empresa',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut empresa',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        type="subHeader"
        title="Esta es toda tu red de empresas."
        variant="body1"
        buttons={[
          {
            name: 'Buscar',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={loading}
              />
            ),
          },
        ]}
      />

      <TableWithPagination
        dataHeaders={headers}
        searchData={searchNetwork}
        data={businesses}
        setData={setBusinesses}
        showActions
        rowsPerPageDefault={PAGE_SIZE}
        actions={actions}
        pagination={pagination}
        setPagination={setPagination}
        lastElement
      />
    </div>
  );
};

export default NetworkBusinesses;
